import {
    Alert,
    Grid,
    Typography,
    useTheme,
    PargodaInfoIcon,
    Tab,
    Tabs,
    Select,
    MenuItem,
    RootState,
    SelectChangeEvent,
} from '../../../base';
import { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import moment from 'moment';
import {
    a11yProps,
    ApplyNowFormValues,
    createScoreRangeConfig,
    evaluatedRequirementLabels,
    FeatureFlags,
    IApplyNowAdditionalQuestionsForm,
    IApplyQuestion,
    IJobApplicationRequirement,
    IRequirementsData,
    IQuestionAnswer,
    IRangeBlocksValues,
    QuestionAnswerFields,
    QuestionMode,
    QuestionType,
    RequirementLevels,
    TooltipPlacement,
} from '../../../common';
import SkillBar from './SkillBar';
import { CardWrapper, StyledTabPanel } from './EvaluatedSkills.styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { jobApplicationActions, organizationSelectors, userSelectors } from '../../../actions';
import { QuestionsList } from '../../SaveJob/SaveJobSteps/JobQuestions/components';

const AI_SOURCE_KEY = 'ai';

interface Props {
    requirements: IRequirementsData | null | undefined;
    isEditable?: boolean;
    questionAnswers?: Array<IQuestionAnswer>;
    isLegacy?: boolean;
}

const EvaluatedSkills = (props: Props) => {
    const { requirements, isEditable = false, questionAnswers, isLegacy = false } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [tabValue, setTabValue] = useState(0);
    const [selectedSource, setSelectedSource] = useState<string>(AI_SOURCE_KEY);
    const [hasError, setHasError] = useState(false);
    const [mandatoryRequirements, setMandatoryRequirements] = useState<
        Array<IJobApplicationRequirement>
    >([]);
    const [advantageRequirements, setAdvantageRequirements] = useState<
        Array<IJobApplicationRequirement>
    >([]);
    const [regularRequirements, setRegularRequirements] = useState<
        Array<IJobApplicationRequirement>
    >([]);
    const scoreRangeConfig = createScoreRangeConfig(evaluatedRequirementLabels);

    const isCandidateQuestionsFeatureFlagOn = useSelector((state: RootState) =>
        organizationSelectors.isFlagOn(state, FeatureFlags.CANDIDATE_QUESTIONS)
    );
    const myUser = useSelector((state: RootState) =>
        userSelectors.getMyUser(state)
    );

    const sourceKeys = useMemo(() => {
        if (!requirements || typeof requirements !== 'object') return [AI_SOURCE_KEY];
        const keys = Object.keys(requirements);
        return [AI_SOURCE_KEY, ...keys.filter((k) => k !== AI_SOURCE_KEY)];
    }, [requirements]);

    const getSourceLabel = (key: string) => {
        if (key === AI_SOURCE_KEY) return t('jobApplicationPage.ai', 'AI');
        if (myUser && String(myUser.id) === key) return t('jobApplicationPage.you', 'You');
        return `${t('jobApplicationPage.reviewer', 'Reviewer')} ${key}`;
    };

    const applyQuestions: Array<IApplyQuestion> = useMemo(() => {
        return questionAnswers?.map((qa) => qa.applyQuestion) || [];
    }, [questionAnswers]);

    const convertAnswerToFormValue = (qa: IQuestionAnswer) => {
        const { applyQuestion, answer, selectedOptions, fileUrl, fileName } =
            qa;

        switch (applyQuestion.type) {
            case QuestionType.SINGLE_SELECT:
                return selectedOptions?.[0]?.value !== undefined
                    ? String(selectedOptions[0].value)
                    : '';
            case QuestionType.MULTIPLE_SELECT:
                return selectedOptions?.map((opt) => String(opt.value)) || [];
            case QuestionType.BOOLEAN:
                return answer !== undefined && answer !== null
                    ? String(answer)
                    : '';
            case QuestionType.NUMERIC:
                return answer !== undefined && answer !== null
                    ? String(answer)
                    : '';
            case QuestionType.DATE_RANGE:
                if (answer) {
                    const parts = answer.split(' to ');
                    return {
                        from: parts[0] || null,
                        to: parts[1] || null,
                    };
                }
                return { from: null, to: null };
            case QuestionType.SELECT_DATE:
                return answer ? moment(answer) : null;
            case QuestionType.FILE_UPLOAD:
                return fileUrl ? { fileUrl, fileName } : null;
            default:
                return answer || '';
        }
    };

    const defaultFormValues = useMemo(() => {
        const questionAnswerValues =
            questionAnswers?.map((qa) => ({
                [QuestionAnswerFields.QUESTION_ID]: qa.applyQuestion.id,
                [QuestionAnswerFields.ANSWER]: convertAnswerToFormValue(qa),
            })) || [];

        return {
            [ApplyNowFormValues.acceptTerms]: true,
            [ApplyNowFormValues.questionAnswers]: questionAnswerValues,
        };
    }, [questionAnswers]);

    const formMethods = useForm<IApplyNowAdditionalQuestionsForm>({
        defaultValues: defaultFormValues as IApplyNowAdditionalQuestionsForm,
    });

    useEffect(() => {
        try {
            const sourceData = requirements?.[selectedSource] ?? {};
            setMandatoryRequirements(
                (sourceData[RequirementLevels.mandatory] ?? []).map((r) => ({
                    ...r,
                    user_requirement_scores: r.user_requirement_scores ?? [],
                }))
            );
            setAdvantageRequirements(
                (sourceData[RequirementLevels.advantage] ?? []).map((r) => ({
                    ...r,
                    user_requirement_scores: r.user_requirement_scores ?? [],
                }))
            );
            setRegularRequirements(
                (sourceData[RequirementLevels.regular] ?? []).map((r) => ({
                    ...r,
                    user_requirement_scores: r.user_requirement_scores ?? [],
                }))
            );
            setHasError(false);
        } catch {
            setHasError(true);
        }
    }, [requirements, selectedSource]);

    const handleBlockChange = (
        requirementId: number,
        value: IRangeBlocksValues
    ) => {
        const score = value.to * 10;
        dispatch(
            jobApplicationActions.updateUserRequirementScore({
                job_application_score: requirementId,
                score,
            })
        );
    };

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: number
    ) => {
        setTabValue(newValue);
    };

    const handleSourceChange = (event: SelectChangeEvent) => {
        setSelectedSource(event.target.value);
    };

    const renderSourceDropdown = () => (
        <Select
            value={selectedSource}
            onChange={handleSourceChange}
            variant="standard"
            sx={{
                width: 'fit-content',
                fontSize: theme.typography.caption.fontSize,
                color: theme.palette.text.secondary,
                '& .MuiSelect-select': { paddingTop: 0, paddingBottom: '2px' },
                '& .MuiSvgIcon-root': { fontSize: '1rem' },
            }}
        >
            {sourceKeys.map((key) => (
                <MenuItem key={key} value={key}>
                    {getSourceLabel(key)}
                </MenuItem>
            ))}
        </Select>
    );

    const renderRequirementsContent = () => {
        if (hasError) {
            return (
                <Grid
                    container
                    alignItems="center"
                    justifyContent="center"
                    sx={{ flex: '1 1 0', minHeight: 0 }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                    >
                        {t(
                            'jobApplicationPage.requirementsLoadError',
                            "We're having issues loading this"
                        )}
                    </Typography>
                </Grid>
            );
        }

        return (
            <Grid container sx={{ flex: '1 1 0', overflowY: 'auto', minHeight: 0 }}>
                {mandatoryRequirements?.map((mandatoryRequirement) => (
                    <SkillBar
                        isEditable={isEditable}
                        key={mandatoryRequirement.name}
                        requirement={mandatoryRequirement}
                        requirementLevel={t('jobApplicationPage.mandatory')}
                        scoreRangeConfig={scoreRangeConfig}
                        handleBlockChange={handleBlockChange}
                    />
                ))}
                {regularRequirements?.map((regularRequirement) => (
                    <SkillBar
                        isEditable={isEditable}
                        key={regularRequirement.name}
                        requirement={regularRequirement}
                        requirementLevel={t('jobApplicationPage.regular')}
                        scoreRangeConfig={scoreRangeConfig}
                        handleBlockChange={handleBlockChange}
                    />
                ))}
                {advantageRequirements?.map((advantageRequirement) => (
                    <SkillBar
                        isEditable={isEditable}
                        key={advantageRequirement.name}
                        requirement={advantageRequirement}
                        requirementLevel={t('jobApplicationPage.advantage')}
                        scoreRangeConfig={scoreRangeConfig}
                        handleBlockChange={handleBlockChange}
                    />
                ))}
            </Grid>
        );
    };

    const renderQuestionsContent = () => (
        <Grid container sx={{ flex: '1 1 0', overflowY: 'auto', minHeight: 0 }}>
            <Grid item xs={12}>
                <FormProvider {...formMethods}>
                    <QuestionsList
                        control={formMethods.control}
                        mode={QuestionMode.VIEW}
                        applyQuestions={applyQuestions}
                        showDealbreakerInfo={true}
                    />
                </FormProvider>
            </Grid>
        </Grid>
    );

    const showQuestions =
        isCandidateQuestionsFeatureFlagOn && applyQuestions.length > 0;

    return (
        <CardWrapper>
            <Grid
                container
                sx={{
                    height: '100%',
                    flexDirection: 'column',
                    flexWrap: 'nowrap',
                }}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ flexShrink: 0 }}
                >
                    <Grid
                        item
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                        <Typography variant="h6">
                            {t('jobApplicationPage.evaluation')}
                        </Typography>
                        <Typography variant="caption">|</Typography>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{
                                flexShrink: 0,
                                '& .Mui-selected': { border: 'none' },
                                paddingBottom: theme.spacing(1),
                                height: '20px',
                                '& .MuiTab-root:not(:last-of-type)': {
                                    marginInlineEnd: theme.spacing(2),
                                },
                            }}
                            TabScrollButtonProps={{ style: { display: 'none' } }}
                        >
                            <Tab
                                label={t('jobApplicationPage.requirements')}
                                {...a11yProps(0)}
                            />
                            <Tab
                                label={t('jobApplicationPage.questions')}
                                disabled={!showQuestions}
                                {...a11yProps(1)}
                            />
                        </Tabs>
                    </Grid>
                    <Grid item sx={{ marginInlineEnd: '-5px' }}>
                        <PargodaInfoIcon
                            text={t('jobApplicationPage.evaluatedRequirementsInfo')}
                            placement={TooltipPlacement.TOP_START}
                            size={24}
                        />
                    </Grid>
                </Grid>
                {isLegacy ? (
                    <Alert severity="info" variant="outlined" sx={{ mt: 'auto', mb: 'auto' }}>
                        {t('jobApplicationPage.legacyRequirementsNotice')}
                    </Alert>
                ) : (
                    <>
                        {tabValue === 0 && renderSourceDropdown()}
                        <StyledTabPanel value={tabValue} index={tabValue}>
                            {tabValue === 0 && renderRequirementsContent()}
                            {tabValue === 1 && showQuestions && renderQuestionsContent()}
                        </StyledTabPanel>
                    </>
                )}
            </Grid>
        </CardWrapper>
    );
};

export default EvaluatedSkills;
